/**
 * @file Custom valibot types for data validation.
 * The schemas defined here are used throughout the application to ensure data integrity.
 * They always start with the prefix "vc" (valibot custom).
 */

import * as v from 'valibot';
import { BaseIssue } from 'valibot';

import { newSchema } from './schema';

/**
 * A valibot schema for validating two-letter uppercase ISO country codes.
 * Attention: This schema does not validate whether the code is an actual country code,
 * only that it consists of exactly two uppercase letters.
 * Examples: "US", "DE", "FR"
 */
export const vcTwoLetterCountryCode = newSchema(
  'vcTwoLetterCountryCode',
  v.pipe(v.string(), v.regex(/^[A-Z]{2}$/, 'The field needs to be the two letter country ISO code in uppercase')),
);

/**
 * A valibot schema for validating GCIDs.
 * GCIDs are represented as UUIDs.
 */
export const vcGcid = newSchema('vcGcid', v.pipe(v.string(), v.uuid('GCID must be a valid UUID')));

/**
 * A valibot schema for validating UCIDs.
 * UCIDs are alphanumeric strings with a maximum length of 18 characters.
 * Examples: "234-42a5"
 */
export const vcUcid = newSchema(
  'vcUcid',
  v.pipe(
    v.string(),
    v.minLength(1, 'UCID can not be empty'),
    v.maxLength(18, 'UCID can be 18 characters long at most'),
    v.regex(/^[A-Za-z0-9-]+$/, 'UCID can only contain alphanumeric characters and hyphens'),
  ),
);

/**
 * A valibot schema for validating bmwOrMiniId, which is an email address.
 */
export const vcBmwOrMiniId = newSchema('vcBmwOrMiniId', v.pipe(v.string(), v.email()));

/**
 * A valibot schema for validating Account IDs.
 * Examples: "001KD000003JwDOYA0"
 */
export const vcAccountId = newSchema(
  'vcAccountId',
  v.pipe(
    v.string(),
    v.minLength(1, 'Account ID cannot be empty'),
    v.regex(/^[a-zA-Z0-9]+$/, 'Account ID can only contain letters and digits'),
  ),
);

/**
 * A valibot schema for validating Account Numbers.
 * Examples: "DEI1001J1Y"
" */
export const vcAccountNumber = newSchema(
  'vcAccountNumber',
  v.pipe(
    v.string(),
    v.minLength(1, 'Account Number cannot be empty'),
    v.regex(/^[A-Z0-9]+$/, 'Account Number can only contain uppercase letters and digits'),
  ),
);

/**
 * A valibot schema for validating Vehicle Identification Numbers (VINs).
 * VINs are exactly 17 characters long and consist of letters (except I, O, Q) and digits only.
 * Examples: "1HGCM82633A123456", "WBA3A5C50DF123456"
 */
export const vcVin = newSchema(
  'vcVin',
  v.pipe(
    v.string(),
    v.length(17, 'VIN must be exactly 17 characters long'),
    v.regex(/^[A-HJ-NPR-Z0-9]{17}$/, 'VIN must consist of letters (except I, O, Q) and digits only'),
  ),
);

/**
 * A valibot transform action that converts a string to Capital Case format.
 * Similar to v.toUpperCase().
 * "REDUCED" => "Reduced"
 * "sOmeExample" => "Someexample"
 * "sOme Example with bLanks" => "Some Example With Blanks"
 *
 * @example
 * v.pipe(
 *   v.string(),
 *   vctToCapitalCase,
 *   v.enum(MyEnum),
 * )
 */
export const vcToCapitalCase: v.TransformAction<string, string> = v.transform((text) => {
  return text
    .trim()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
});

/**
 * A valibot enum schema that transforms input strings to Capital Case before validation as enum.
 * "REDUCED" => "Reduced"
 * "someexample" => "Someexample"
 * "foo_BAr" => "Foo_bar"
 *
 * @param e The enum object to validate against.
 */
export function vcEnumCapitalCase<const T extends Record<string, string>>(e: T) {
  return v.pipe(v.string(), vcToCapitalCase, v.enum(e));
}

/**
 * A valibot enum schema that transforms input strings to uppercase before validation as enum.
 * "Reduced" => "REDUCED"
 * "someexample" => "SOMEEXAMPLE"
 * "foo_BAr" => "FOO_BAR"
 *
 * @param e The enum object to validate against.
 */
export function vcEnumUpperCase<const T extends Record<string, string>>(e: T) {
  return v.pipe(
    v.string(),
    v.transform((v) => v.toUpperCase()),
    v.enum(e),
  );
}

/**
 * A valibot enum schema that transforms input strings to lowercase before validation as enum.
 * "Reduced" => "reduced"
 * "someexample" => "someexample"
 * "foo_BAr" => "foo_bar"
 *
 * @param e The enum object to validate against.
 */
export function vcEnumLowerCase<const T extends Record<string, string>>(e: T) {
  return v.pipe(
    v.string(),
    v.transform((v) => v.toLowerCase()),
    v.enum(e),
  );
}

/**
 * A valibot schema that makes a schema optional and transforms null to undefined.
 *
 * @param schema The schema to make optional and transform null to undefined.
 */
export function vcOptionalWithNullToUndefined<T extends v.BaseSchema<unknown, unknown, BaseIssue<unknown>>>(schema: T) {
  return v.pipe(
    v.nullish(schema),
    v.transform((value) => value ?? undefined),
  );
}
